/**
 * ONE definition of "a complete collection / folder / request", and one place
 * where a client's arguments are checked.
 *
 * ## Delegate, never re-type
 *
 * The shaping helpers already exist and are pure — `collectionFolders`,
 * `requestMappers`, `requestAuth`, `requestBodyMode`,
 * `collectionImport/parseImportFile`. This module joins them; it does not
 * reimplement them. Re-typing a field list is how a record silently loses
 * `mock`, `sort_order` or `settings` on the next save, which is the failure
 * `requestMappers.js`'s own shape exists to prevent.
 *
 * ## Why validation happens HERE as well as in the schema
 *
 * `dispatch::validate_against_schema` implements neither `format` nor
 * `minLength`/`minimum`, and it cannot express a cross-field rule at all
 * (recorded in phase 2 tranche 1, deviation 5). Everything below the schema's
 * expressive power is therefore re-checked in JS. Where the schema CAN express
 * a rule (the `method` enum, `additionalProperties: false`) it is stated in
 * both places on purpose: the Rust check is the one a client sees first, and
 * the JS check is the one that still holds if a handler is ever driven from
 * somewhere else.
 *
 * ## The two refusals that make this phase's promise real (**O32**)
 *
 * 1. **A literal credential in an auth block is REFUSED**, not stored. A
 *    server that promises never to READ a credential must not be the thing
 *    that WRITES one, and the refusal teaches the model the `{{variable}}`
 *    pattern the app itself recommends (`toolSchemas.js:683`: *"Prefer
 *    referencing a variable ({{token}}) over pasting a literal credential"*).
 *    This makes MCP STRICTER than Apiboo's own editor, where a human may type
 *    a literal. The cost, accepted: an agent copying a curl example verbatim
 *    is refused once and must create a variable first.
 * 2. **Userinfo in a url is REFUSED, never silently stripped.** An agent
 *    writing `https://u:p@host` has just tried to persist a credential and
 *    must be told, not quietly corrected — a silent strip would leave it
 *    believing the request works.
 *
 * `oauth2` and `awssigv4` are not accepted at all in this phase: every field
 * they have is a credential field, so "accept the type but refuse every
 * literal" would be a type nothing could usefully be set on.
 */
import { createFolderInCollection, getFolders, normalizeParentId } from '../utils/collectionFolders';
import { defaultRequestAuth, defaultCollectionAuth } from '../utils/requestAuth';
import { normalizeRequestBodyMode } from '../utils/requestBodyMode';
import { generateLocalEntityId } from '../utils/localIds';
import { mcpError } from './errors';
import { isPointerOnly } from './templateRefs';

/** The app's own set (`utils/collectionImport/normalize.js:3`). */
export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

/** Accepted auth types. `oauth2` / `awssigv4` are out this phase — see header. */
export const AUTH_TYPES = ['inherit', 'noauth', 'bearer', 'apikey', 'basic'];

/**
 * Every auth field whose value is a credential, by `type`. The map is the
 * contract: a type with an entry here has its listed fields checked against
 * `isPointerOnly`, and a field NOT listed (an api-key's `key`, a basic auth's
 * `username`, the `in` selector) is a name or a position, not a secret.
 */
const AUTH_CREDENTIAL_FIELDS = Object.freeze({
  bearer: ['bearer'],
  apikey: ['apikey.value'],
  basic: ['basic.password'],
});

/**
 * Userinfo before the first path segment: `scheme://anything@host`.
 *
 * Widened from `utils/redactResponse.js:47`'s `URL_CRED_RE`, which requires a
 * `user:pass` PAIR. `https://ghp_xxx@github.com/...` is a single-token
 * credential with no colon and is exactly as much of a leak, so the check here
 * is "is there an `@` in the authority at all".
 */
const URL_USERINFO_RE = /^[a-z][a-z0-9+.-]*:\/\/[^/?#\s]*@/i;

/** `{{x}}` → an inert token, so a templated url can still be shape-checked. */
const TEMPLATE_RE = /\{\{\s*[^}]+\s*\}\}/g;
const TEMPLATE_PLACEHOLDER = 'apiboovar';

function bad(message, extra) {
  return mcpError('invalid_argument', message, extra || {});
}

function asString(value) {
  return typeof value === 'string' ? value : '';
}

/* ─────────────────────────── argument checks ─────────────────────────── */

export function checkMethod(method) {
  const m = asString(method).toUpperCase();
  if (!HTTP_METHODS.includes(m)) {
    return bad(`\`method\` must be one of ${HTTP_METHODS.join(', ')}.`, { method: asString(method) });
  }
  return null;
}

/**
 * A url may carry `{{variables}}` anywhere, including as the whole host — a
 * request whose url is `{{baseUrl}}/v1/users` is the shape Apiboo itself
 * recommends, so an absolute-URL requirement would refuse the common case.
 * What is checked is that it is non-empty, has no whitespace, and carries no
 * userinfo.
 */
export function checkUrl(url) {
  const raw = asString(url).trim();
  if (!raw) return bad('`url` is required and must not be empty.');
  if (/\s/.test(raw)) return bad('`url` must not contain whitespace.');
  if (URL_USERINFO_RE.test(raw)) {
    return bad(
      'That url carries credentials in it (user:password@host). Nothing was written. Put the credential in an Apiboo variable and reference it as {{name}}, or leave it out and let the user add it.'
    );
  }
  const probe = raw.replace(TEMPLATE_RE, TEMPLATE_PLACEHOLDER);
  if (URL_USERINFO_RE.test(probe)) {
    return bad(
      'That url carries credentials in it once its variables are substituted. Nothing was written.'
    );
  }
  return null;
}

/** `[{key, value, enabled}]`, names always required, values free. */
export function checkKvRows(rows, label) {
  if (rows == null) return null;
  if (!Array.isArray(rows)) return bad(`\`${label}\` must be an array of {key, value}.`);
  if (rows.length > 100) return bad(`\`${label}\` may hold at most 100 entries.`);
  for (const row of rows) {
    if (!row || typeof row !== 'object') return bad(`Every \`${label}\` entry must be an object.`);
    const key = asString(row.key).trim();
    if (!key) return bad(`Every \`${label}\` entry needs a non-empty \`key\`.`);
    // RFC 9110 field-name charset for headers; params are looser but a control
    // character in either is a request nobody can send.
    if (label === 'headers' && !/^[A-Za-z0-9!#$%&'*+\-.^_`|~]+$/.test(key)) {
      return bad(`\`${key}\` is not a legal HTTP header name.`);
    }
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f]/.test(asString(row.value))) {
      return bad(`The value of \`${key}\` contains a control character.`);
    }
  }
  return null;
}

export function checkBody(body) {
  if (body == null) return null;
  if (typeof body !== 'object' || Array.isArray(body)) {
    return bad('`body` must be an object: {mode, language, content}.');
  }
  const mode = body.mode == null ? 'raw' : asString(body.mode);
  const modes = ['none', 'raw', 'form-data', 'urlencoded', 'graphql'];
  if (!modes.includes(mode)) return bad(`\`body.mode\` must be one of ${modes.join(', ')}.`);
  if (body.language != null && !['text', 'json', 'xml'].includes(asString(body.language))) {
    return bad('`body.language` must be one of text, json, xml.');
  }
  if (body.content != null && typeof body.content !== 'string') {
    return bad('`body.content` must be a string.');
  }
  return null;
}

/**
 * **O32.** The write-side twin of the secrets contract: an auth block may be
 * WIRED to variables and may be left empty, but it may not carry a literal.
 */
export function checkAuth(auth) {
  if (auth == null) return null;
  if (typeof auth !== 'object' || Array.isArray(auth)) {
    return bad('`auth` must be an object.');
  }
  const type = auth.type == null ? 'inherit' : asString(auth.type);
  if (!AUTH_TYPES.includes(type)) {
    return bad(
      `\`auth.type\` must be one of ${AUTH_TYPES.join(', ')}. oauth2 and awssigv4 cannot be set over MCP: every field they have is a credential field.`,
      { type }
    );
  }
  // EVERY credential path is checked, not just the ones belonging to the
  // declared `type`. That is deliberate and it closes a real hole:
  // `{type: 'noauth', bearer: 'sk-live-...'}` would otherwise be stored
  // verbatim and become live the moment the user switched the type in the
  // editor. The map is iterated whole for that reason.
  for (const paths of Object.values(AUTH_CREDENTIAL_FIELDS)) {
    for (const path of paths) {
      const value = readPath(auth, path);
      if (value == null) continue;
      if (typeof value !== 'string') return bad(`\`auth.${path}\` must be a string.`);
      if (!isPointerOnly(value)) {
        return bad(
          `\`auth.${path}\` looks like a literal credential, and this server will not store one. Nothing was written. Create an Apiboo variable for it and pass a reference instead, for example "{{apiToken}}" — then tell the user which variable to fill in.`,
          { field: `auth.${path}` }
        );
      }
    }
  }
  return null;
}

function readPath(obj, path) {
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

/** Free text the user (or the agent, as documentation) may set. */
export function checkText(value, label, max = 20000) {
  if (value == null) return null;
  if (typeof value !== 'string') return bad(`\`${label}\` must be a string.`);
  if (value.length > max) return bad(`\`${label}\` is longer than ${max} characters.`);
  return null;
}

/* ─────────────────────────── record builders ─────────────────────────── */

/**
 * A complete collection record, in the shape `handleCreateCollection`'s local
 * branch writes (`useCollections.js:452-456`) plus the folder container and
 * the collection auth the editor expects.
 *
 * `markLocalOnly` is deliberately NOT used: it lives in
 * `services/localCollectionLink.js` and its only effect here would be a
 * `local_uid` and a `link_status`, both of which describe cloud LINKING — a
 * concept this surface refuses to touch (O27 refuses cloud-REST workspaces
 * outright). Naming it rather than copying it, because its absence is a
 * decision.
 */
export function buildCollection({ name, description }) {
  return {
    id: generateLocalEntityId('col'),
    name: String(name).trim(),
    description: asString(description),
    settings: { folders: [], collection_auth: defaultCollectionAuth() },
    requests: [],
  };
}

/**
 * A folder, through the app's OWN `createFolderInCollection` — which is what
 * computes `sort_order` from the existing siblings. Returns the next
 * collection plus the folder that was added.
 */
export function addFolder(collection, { name, parentFolderId, description }) {
  const before = new Set(getFolders(collection).map((f) => String(f.id)));
  const next = createFolderInCollection(collection, {
    name: String(name).trim(),
    parent_id: normalizeParentId(parentFolderId),
  });
  const folder = getFolders(next).find((f) => !before.has(String(f.id)));
  if (!folder) return { next: collection, folder: null };
  const described = asString(description)
    ? getFolders(next).map((f) =>
        String(f.id) === String(folder.id) ? { ...f, description: asString(description) } : f
      )
    : null;
  return {
    next: described
      ? { ...next, settings: { ...(next.settings || {}), folders: described } }
      : next,
    folder: described ? described.find((f) => String(f.id) === String(folder.id)) : folder,
  };
}

/**
 * A complete request record, in the shape the local save path writes
 * (`useCollections.js:1188-1205`). Every field that path sets is set here, for
 * the reason in the header: a field left out does not merely default, it stops
 * existing on the next save and on every device.
 */
export function buildRequest(args, { sortOrder }) {
  const mode = normalizeRequestBodyMode({
    bodyType: args.body?.mode ?? 'none',
    rawBodyLanguage: args.body?.language,
  });
  const rows = (list) =>
    (Array.isArray(list) ? list : [])
      .filter((r) => asString(r.key).trim())
      .map((r) => ({
        key: asString(r.key),
        value: asString(r.value),
        enabled: r.enabled !== false,
      }));
  const headers = rows(args.headers);
  const params = rows(args.params);
  return {
    id: generateLocalEntityId('req'),
    name: asString(args.name).trim() || `${String(args.method).toUpperCase()} ${args.url}`,
    method: String(args.method).toUpperCase(),
    url: asString(args.url).trim(),
    // The editor renders one blank row when a list is empty; matching that
    // here keeps an MCP-created request byte-identical to a hand-made one.
    headers: headers.length ? headers : [{ key: '', value: '', enabled: true }],
    params: params.length ? params : [{ key: '', value: '', enabled: true }],
    cookies: [{ key: '', value: '', enabled: true }],
    body: asString(args.body?.content),
    bodyType: mode.bodyType,
    rawBodyLanguage: mode.rawBodyLanguage,
    formDataFields: undefined,
    auth: mergeAuth(args.auth),
    description: asString(args.description),
    mock: { responseExamples: [] },
    // **O33.** Scripts are neither readable nor writable over MCP: a
    // pre-request script runs with `pm.*` access on every send, so writing one
    // is arranging for code to execute later with the user's variables. They
    // are set to the empty string the editor expects, never from an argument.
    preRequestScript: '',
    testScript: '',
    type: 'http',
    sort_order: sortOrder,
    folder_id: normalizeParentId(args.folderId),
    settings: {},
  };
}

/**
 * Tranche 3 — the same record shape, applied as a PATCH to a request that
 * already exists.
 *
 * Only the keys the client actually sent are touched; every other field of the
 * stored record is carried through untouched, INCLUDING the ones this server
 * may not read — `preRequestScript`, `testScript` (**O33**) and `mock`. That is
 * the whole reason this is a patch rather than a rebuild: `buildRequest` would
 * reset a script the user wrote to `''`, which is a silent destructive edit
 * made by a tool that promised not to touch scripts at all.
 *
 * `id`, `sort_order`, `folder_id` and `type` are never patchable here. A move
 * is `request_move` (it reorders siblings, which an update must not do), and a
 * cross-collection move is out of scope entirely (§I).
 */
export function applyRequestPatch(request, args) {
  const next = { ...request };
  const has = (key) => Object.prototype.hasOwnProperty.call(args, key) && args[key] != null;

  if (has('name')) next.name = asString(args.name).trim() || next.name;
  if (has('method')) next.method = String(args.method).toUpperCase();
  if (has('url')) next.url = asString(args.url).trim();
  if (has('description')) next.description = asString(args.description);

  const rows = (list) =>
    (Array.isArray(list) ? list : [])
      .filter((r) => asString(r.key).trim())
      .map((r) => ({
        key: asString(r.key),
        value: asString(r.value),
        enabled: r.enabled !== false,
      }));
  // A supplied list REPLACES the stored one — it is not merged row by row.
  // Merging would make "remove this header" impossible to express, and the
  // tool description says `headers` and `params` replace.
  if (has('headers')) {
    const list = rows(args.headers);
    next.headers = list.length ? list : [{ key: '', value: '', enabled: true }];
  }
  if (has('params')) {
    const list = rows(args.params);
    next.params = list.length ? list : [{ key: '', value: '', enabled: true }];
  }
  if (has('body')) {
    const mode = normalizeRequestBodyMode({
      bodyType: args.body?.mode ?? 'none',
      rawBodyLanguage: args.body?.language,
    });
    next.body = asString(args.body?.content);
    next.bodyType = mode.bodyType;
    next.rawBodyLanguage = mode.rawBodyLanguage;
  }
  if (has('auth')) next.auth = mergeAuth(args.auth);

  return next;
}

/** The fields `request_update` will act on, in the order they are checked. */
export const PATCHABLE_REQUEST_FIELDS = Object.freeze([
  'name',
  'method',
  'url',
  'description',
  'headers',
  'params',
  'body',
  'auth',
]);

/** The client's auth over the app's own default, so no field goes missing. */
function mergeAuth(auth) {
  const base = defaultRequestAuth();
  if (!auth || typeof auth !== 'object') return base;
  return {
    ...base,
    type: auth.type ? String(auth.type) : base.type,
    bearer: asString(auth.bearer),
    apikey: { ...base.apikey, ...(auth.apikey || {}) },
    basic: { ...base.basic, ...(auth.basic || {}) },
  };
}

/** The next free sort_order among a parent's children. */
export function nextSortOrder(collection, folderId) {
  const pid = normalizeParentId(folderId);
  const orders = [
    ...getFolders(collection)
      .filter((f) => normalizeParentId(f.parent_id) === pid)
      .map((f) => (typeof f.sort_order === 'number' ? f.sort_order : 0)),
    ...(collection.requests || [])
      .filter((r) => normalizeParentId(r.folder_id) === pid)
      .map((r) => (typeof r.sort_order === 'number' ? r.sort_order : 0)),
  ];
  return orders.length ? Math.max(...orders) + 1 : 0;
}

/**
 * The human path to a node — `{collectionName, folderPath: [...]}` — returned
 * on every create.
 *
 * **O36: report the path, never navigate.** The sidebar's expansion state is
 * component `useState` inside `Sidebar.js` (`:871-872`), unreachable from
 * here, so this surface can neither reveal a node nor honestly report whether
 * it is visible. What it CAN do is name where the thing went, so the agent can
 * tell the user where to look instead of assuming they can see it. Yanking
 * their window to another panel is a UI action nobody asked for — see
 * `mcp/uiRefresh.js`.
 */
export function pathOf(collection, folderId) {
  const folders = getFolders(collection);
  const byId = new Map(folders.map((f) => [String(f.id), f]));
  const chain = [];
  const seen = new Set();
  let current = normalizeParentId(folderId);
  while (current && !seen.has(String(current))) {
    seen.add(String(current));
    const folder = byId.get(String(current));
    if (!folder) break;
    chain.unshift(String(folder.name || ''));
    current = normalizeParentId(folder.parent_id);
  }
  return { collectionName: String(collection.name || ''), folderPath: chain };
}

/** `not_found`, phrased so a model's next move is to list rather than retry. */
export function collectionNotFound(collectionId) {
  return mcpError(
    'not_found',
    'No collection with that id in the open workspace. Nothing was written. Call collections_list and use an id it returns.',
    { collectionId: String(collectionId ?? '') }
  );
}
